import { SimpleMerkleTree } from '@openzeppelin/merkle-tree'
import { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import {
  encodeAbiParameters,
  Hex,
  keccak256,
  parseUnits,
  zeroAddress,
} from 'viem'
import {
  EntryPointVersion,
  UserOperation,
  entryPoint07Address,
  getUserOperationHash,
} from 'viem/account-abstraction'

import {
  BackendTransaction,
  BasicTransactionType,
  SmartAccountType,
} from '~/types'
import { Address, EvmAddress } from '~/utils/address'
import { captureException } from '~/utils/errors'
import { convertChallengeToOriginalHash } from '~/utils/smartAccounts/index'
import { getTokenTransferBytes } from '~/utils/tokenTransfer'

export const getTransactionUrl = (
  transaction: Pick<BackendTransaction, 'smartAccountAddress' | 'id'>,
) =>
  `${window.location.origin}/accounts/${transaction.smartAccountAddress}/transactions/${transaction.id}/`

export const getTransferCalls = ({
  token,
  recipients,
}: {
  token: EvmAddress.Type
  recipients: {
    address: EvmAddress.Type
    amount: bigint
  }[]
}) => {
  return recipients.map(({ address, amount }) => ({
    to: Address.equal(token, zeroAddress) ? address : token,
    value: Address.equal(token, zeroAddress) ? amount : BigInt(0),
    data: Address.equal(token, zeroAddress)
      ? '0x'
      : getTokenTransferBytes(address, amount),
  }))
}

const getTransactionTypes = ({
  isTransfer,
  isSwapping,
  isBridging,
  isEarning,
}: {
  isTransfer: boolean
  isSwapping: boolean
  isBridging: boolean
  isEarning: boolean
}): BasicTransactionType[] =>
  [
    isTransfer && BasicTransactionType.Transfer,
    isSwapping && BasicTransactionType.Swap,
    isBridging && BasicTransactionType.Bridge,
    isEarning && BasicTransactionType.Earn,
  ].filter((t): t is BasicTransactionType => !!t)

export const getSwapTransferTransactionTypes = ({
  swapData,
  isBridging,
  earnToken,
}: {
  swapData?: { swapRouter?: unknown }
  isBridging: boolean
  earnToken?: unknown
}): BasicTransactionType[] =>
  getTransactionTypes({
    isTransfer: true,
    isSwapping: !!swapData?.swapRouter,
    isBridging,
    isEarning: !!earnToken,
  })

export const getSwapTransferBridgeStateChanges = ({
  transferToken,
  transactionChainId,
  transferTokenMetadata,
  recipients,
  isPrivate,
}: {
  transferToken?: { token: string; chainId: number }
  transactionChainId?: number
  transferTokenMetadata?: { decimals: number }
  recipients?: {
    receiver?: { address?: string }
    amount: string
  }[]
  isPrivate?: boolean
}) => {
  // Synthesize the recipients' incoming state changes when the simulation
  // can't see them: bridging settles on another chain, and private transfers
  // pay out from the provider in a separate transaction — even same-chain.
  const isSimulationBlind =
    transactionChainId !== transferToken?.chainId || isPrivate
  if (
    !transferToken ||
    !isSimulationBlind ||
    !transferTokenMetadata ||
    !recipients ||
    recipients.length === 0
  ) {
    return undefined
  }

  return recipients.map((recipient) => ({
    address: recipient.receiver?.address as string,
    token: transferToken.token,
    chainId: transferToken.chainId,
    amount: recipient.amount
      ? parseUnits(recipient.amount, transferTokenMetadata.decimals)
      : BigInt(0),
  }))
}

export const verifyAuthOptions = ({
  options,
  transaction,
  isLastSigner,
}: {
  options: PublicKeyCredentialRequestOptionsJSON
  transaction: BackendTransaction
  isLastSigner: boolean
}): boolean => {
  if (transaction.isMerkleTransaction) {
    return verifyAuthOptionsForMerkleTransaction({
      transaction,
      options,
      isLastSigner,
    })
  } else {
    return verifyAuthOptionsForTransaction({
      options,
      transaction,
      isLastSigner,
    })
  }
}

const verifyAuthOptionsForMerkleTransaction = ({
  options,
  transaction,
  isLastSigner,
}: {
  options: PublicKeyCredentialRequestOptionsJSON
  transaction: BackendTransaction
  isLastSigner: boolean
}): boolean => {
  if ('childTransactions' in transaction) {
    const userOps = transaction.childTransactions.map((tx) => {
      return {
        chainId: tx.chainId,
        ...getUserOperationInfo(tx),
      }
    })

    const { merkleTreeInputs, lightMerkleTreeInputs } = getMerkleTreeInputs({
      userOperationsInfo: userOps,
    })

    const message = getSplitsVaultMerkleMessageToSign({
      isLastSigner,
      merkleTreeInputs,
      lightMerkleTreeInputs,
    })

    const originalHash = convertChallengeToOriginalHash(options.challenge)
    return originalHash === message
  } else {
    captureException(
      `no child transactions found for merkle Transaction: ${transaction.id}`,
    )
    return false
  }
}

const verifyAuthOptionsForTransaction = ({
  options,
  transaction,
  isLastSigner,
}: {
  options: PublicKeyCredentialRequestOptionsJSON
  transaction: BackendTransaction
  isLastSigner: boolean
}): boolean => {
  const userOperationInfo = getUserOperationInfo(transaction)

  const message = getSplitsVaultMessageToSign({
    userOperation: userOperationInfo.userOperation,
    isLastSigner,
    chainId: transaction.chainId,
    gasLimits: userOperationInfo.gasLimits,
  })

  const originalHash = convertChallengeToOriginalHash(options.challenge)
  return originalHash === message
}

const getSplitsVaultMessageToSign = ({
  userOperation,
  isLastSigner,
  chainId,
  gasLimits,
}: {
  userOperation: UserOperation<EntryPointVersion>
  isLastSigner: boolean
  chainId: number
  gasLimits?: {
    lightHashCallGasLimit: bigint
    lightHashVerificationGasLimit: bigint
    lightHashPreVerificationGas: bigint
    lightHashMaxPriorityFeePerGas: bigint
    lightHashPaymaster: EvmAddress.Type
    lightHashPaymasterVerificationGasLimit: bigint
    lightHashPaymasterPostOpGasLimit: bigint
  }
}) => {
  if (isLastSigner) {
    const userOperationHash = getUserOperationHash({
      userOperation,
      chainId,
      entryPointAddress: entryPoint07Address,
      entryPointVersion: '0.7',
    })
    return userOperationHash
  }

  if (!gasLimits) throw new Error('Gas limits required for light hash')

  return getLightUserOpHash({
    userOperation,
    chainId,
    gasLimits,
  })
}

const getSplitsVaultMerkleMessageToSign = ({
  isLastSigner,
  merkleTreeInputs,
  lightMerkleTreeInputs,
}: {
  isLastSigner: boolean
  merkleTreeInputs: Hex[]
  lightMerkleTreeInputs: Hex[]
}) => {
  if (isLastSigner) {
    const tree = SimpleMerkleTree.of(merkleTreeInputs)
    return tree.root
  }

  const lightTree = SimpleMerkleTree.of(lightMerkleTreeInputs)
  return lightTree.root
}

const getMerkleTreeInputs = ({
  userOperationsInfo,
}: {
  userOperationsInfo: {
    chainId: number
    userOperation: UserOperation<EntryPointVersion>
    gasLimits: {
      lightHashCallGasLimit: bigint
      lightHashVerificationGasLimit: bigint
      lightHashPreVerificationGas: bigint
      lightHashMaxPriorityFeePerGas: bigint
      lightHashPaymaster: EvmAddress.Type
      lightHashPaymasterVerificationGasLimit: bigint
      lightHashPaymasterPostOpGasLimit: bigint
    }
  }[]
}) => {
  const userOperationHashes = userOperationsInfo.map(
    ({ chainId, userOperation }) =>
      getUserOperationHash({
        userOperation,
        chainId,
        entryPointAddress: entryPoint07Address,
        entryPointVersion: '0.7',
      }),
  )

  const lightUserOperationHashes = userOperationsInfo.map(
    ({ chainId, userOperation, gasLimits }) =>
      getLightUserOpHash({ userOperation, chainId, gasLimits }),
  )

  return {
    merkleTreeInputs: userOperationHashes,
    lightMerkleTreeInputs: lightUserOperationHashes,
  }
}

const getUserOperationInfo = (transaction: BackendTransaction) => {
  const userOperation = constructUserOpFromTransaction(transaction)
  return {
    userOperation,
    gasLimits: {
      lightHashCallGasLimit: BigInt(transaction.lightHashCallGasLimit ?? '0'),
      lightHashVerificationGasLimit: BigInt(
        transaction.lightHashVerificationGasLimit ?? '0',
      ),
      lightHashPreVerificationGas: BigInt(
        transaction.lightHashPreVerificationGas ?? '0',
      ),
      lightHashMaxPriorityFeePerGas: BigInt(
        transaction.lightHashMaxPriorityFeePerGas ?? '0',
      ),
      lightHashPaymaster: EvmAddress.format(
        transaction.lightHashPaymaster ?? NULL_PAYMASTER_ADDRESS,
      ),
      lightHashPaymasterVerificationGasLimit: BigInt(
        transaction.lightHashPaymasterVerificationGasLimit ?? '0',
      ),
      lightHashPaymasterPostOpGasLimit: BigInt(
        transaction.lightHashPaymasterPostOpGasLimit ?? '0',
      ),
    },
  }
}

const getLightUserOpHash = ({
  userOperation,
  chainId,
  gasLimits,
}: {
  userOperation: UserOperation<EntryPointVersion>
  chainId: number
  gasLimits: {
    lightHashCallGasLimit: bigint
    lightHashVerificationGasLimit: bigint
    lightHashPreVerificationGas: bigint
    lightHashMaxPriorityFeePerGas: bigint
    lightHashPaymaster: EvmAddress.Type
    lightHashPaymasterVerificationGasLimit: bigint
    lightHashPaymasterPostOpGasLimit: bigint
  }
}) => {
  const lightHashGasLimits = {
    preVerificationGas: gasLimits.lightHashPreVerificationGas,
    verificationGasLimit: gasLimits.lightHashVerificationGasLimit,
    callGasLimit: gasLimits.lightHashCallGasLimit,
    maxPriorityFeePerGas: gasLimits.lightHashMaxPriorityFeePerGas,
    paymaster: EvmAddress.format(gasLimits.lightHashPaymaster),
    paymasterVerificationGasLimit:
      gasLimits.lightHashPaymasterVerificationGasLimit,
    paymasterPostOpGasLimit: gasLimits.lightHashPaymasterPostOpGasLimit,
  }

  const lightHash = keccak256(
    encodeAbiParameters(
      [{ type: 'address' }, { type: 'uint256' }, { type: 'bytes32' }],
      [
        userOperation.sender,
        userOperation.nonce,
        keccak256(userOperation.callData),
      ],
    ),
  )
  return keccak256(
    encodeAbiParameters(
      [
        { type: 'bytes32' },
        {
          type: 'tuple',
          components: [
            { name: 'maxPriorityFeePerGas', type: 'uint256' },
            { name: 'preVerificationGas', type: 'uint256' },
            { name: 'callGasLimit', type: 'uint256' },
            { name: 'verificationGasLimit', type: 'uint256' },
            { name: 'paymaster', type: 'address' },
            { name: 'paymasterVerificationGasLimit', type: 'uint256' },
            { name: 'paymasterPostOpGasLimit', type: 'uint256' },
          ],
        },
        { type: 'address' },
        { type: 'uint256' },
      ],
      [
        lightHash,
        lightHashGasLimits,
        getEntrypointAddress(SmartAccountType.SPLITS_VAULT_V1),
        BigInt(chainId),
      ],
    ),
  )
}

const constructUserOpFromTransaction = (
  transaction: BackendTransaction,
): UserOperation<EntryPointVersion> => ({
  callData: transaction.callData as Hex,
  callGasLimit: BigInt(transaction.callGasLimit ?? '0'),
  factory: transaction.factory
    ? EvmAddress.format(transaction.factory)
    : undefined,
  factoryData: (transaction.factoryData ?? undefined) as Hex | undefined,
  maxFeePerGas: BigInt(transaction.maxFeePerGas ?? '0'),
  maxPriorityFeePerGas: BigInt(transaction.maxPriorityFeePerGas ?? '0'),
  nonce: BigInt(transaction.nonce ?? '0'),
  paymaster:
    transaction.paymaster === NULL_PAYMASTER_ADDRESS || !transaction.paymaster
      ? undefined
      : EvmAddress.format(transaction.paymaster),
  paymasterData:
    transaction.paymasterData === NULL_PAYMASTER_DATA
      ? undefined
      : (transaction.paymasterData as Hex),
  paymasterVerificationGasLimit: BigInt(
    transaction.paymasterVerificationGasLimit ?? '0',
  ),
  paymasterPostOpGasLimit: BigInt(transaction.paymasterPostOpGasLimit ?? '0'),
  preVerificationGas: BigInt(transaction.preVerificationGas ?? '0'),
  sender: EvmAddress.format(transaction.smartAccountAddress),
  signature: '0x',
  verificationGasLimit: BigInt(transaction.verificationGasLimit ?? '0'),
})

const getEntrypointAddress = (type: SmartAccountType) => {
  if (type === SmartAccountType.SPLITS_VAULT_V1) return entryPoint07Address

  throw new Error(`Unknown smart account type: ${type}`)
}

const NULL_PAYMASTER_ADDRESS = zeroAddress
const NULL_PAYMASTER_DATA = '0x'
